import type { Package } from '../modules/PackageManager';
import type { CookieValue } from 'vanilla-cookieconsent';
import { run, showPreferences } from 'vanilla-cookieconsent';

export default class CookieConsentPackage implements Package {
  setup() {
    const microsoftClarityCookies = [
      {
        name: '_clck',
        category: 'Microsoft Clarity',
        body: 'Uchováva ID používateľa Clarity a preferencie naprieč reláciami.',
      },
      {
        name: '_clsk',
        category: 'Microsoft Clarity',
        body: 'Spája viacero zobrazení stránok používateľa do jednej relácie Clarity.',
      },
      {
        name: 'CLID',
        category: 'Microsoft Clarity',
        body: 'Identifikuje nových používateľov služby Clarity z domény Microsoftu.',
      },
      {
        name: 'MUID',
        category: 'Microsoft (Clarity / Bing Ads)',
        body: 'Identifikuje jedinečné prehliadače na reklamné a analytické účely.',
      },
    ];
    const googleAnalyticsCookies = [
      {
        name: '_ga',
        category: 'Google Analytics',
        body: 'Používa sa na rozlíšenie jednotlivých používateľov.',
      },
      {
        name: '_ga_Y46Z8NGXWB',
        category: 'Google Analytics',
        body: 'Ukladá stav relácie pre konkrétnu vlastnosť Google Analytics 4.',
      },
    ];
    const analyticalCookies = [...microsoftClarityCookies, ...googleAnalyticsCookies];

    const googleAdsCookies = [
      {
        name: '_gcl_au',
        category: 'Google Ads',
        body: 'Používa sa službou Google AdSense na testovanie účinnosti reklám.',
      },
      {
        name: 'ar_debug',
        category: 'Google Ads',
        body: 'Používa sa na ladenie reklám v režime vývojára.',
      },
    ];
    const metaPixelCookies = [
      {
        name: '_fbp',
        category: 'Meta Pixel',
        body: 'Používa sa na poskytovanie reklamných produktov spoločnosti Meta, ako je napr. aukcia v reálnom čase.',
      },
      {
        name: 'c_user',
        category: 'Meta Pixel',
        body: 'Identifikuje prihláseného používateľa Facebooku.',
      },
      {
        name: 'datr',
        category: 'Meta Pixel',
        body: 'Zabezpečuje ochranu pred zneužitím identifikáciou prehliadača.',
      },
      {
        name: 'fr',
        category: 'Meta Pixel',
        body: 'Obsahuje Facebook ID a ID prehliadača na reklamné účely.',
      },
      {
        name: 'presence',
        category: 'Meta Pixel',
        body: 'Zaznamenáva stav aktivity používateľa v rámci chatu.',
      },
      {
        name: 'sb',
        category: 'Meta Pixel',
        body: 'Používa sa na identifikáciu prehliadača a zabezpečenie.',
      },
      {
        name: 'wd',
        category: 'Meta Pixel',
        body: 'Uchováva rozmery okna prehliadača pre optimalizáciu zobrazenia.',
      },
      {
        name: 'xs',
        category: 'Meta Pixel',
        body: 'Ukladá šifrované informácie o relácii používateľa.',
      },
    ];
    const marketingCookies = [...googleAdsCookies, ...metaPixelCookies];

    const createRegex = (cookies: Array<{ name: string }>) => {
      const name = cookies.map((cookie) => cookie.name).join('|');
      return new RegExp(`^(${name})`);
    };

    const baseCookieTableHeaders = {
      name: 'Názov',
      body: 'Popis',
    };

    const cookieTableHeaders = {
      ...baseCookieTableHeaders,
      category: 'Kategória',
    };

    const updateGoogleConsents = (data: { cookie: CookieValue }) => {
      const analytics = data.cookie?.services?.analytics ?? [];
      const marketing = data.cookie?.services?.marketing ?? [];

      const googleAnalyticsAllowed = analytics.includes('googleAnalytics') ? 'granted' : 'denied';
      const googleAdsAllowed = marketing.includes('googleAds') ? 'granted' : 'denied';

      window.gtag('consent', 'update', {
        analytics_storage: googleAnalyticsAllowed,
        ad_storage: googleAdsAllowed,
        ad_user_data: googleAdsAllowed,
        ad_personalization: googleAdsAllowed,
      });
    };

    run({
      categories: {
        necessary: {
          enabled: true, // this category is enabled by default
          readOnly: true, // this category cannot be disabled
        },
        marketing: {
          services: {
            metaPixel: {
              label: 'Meta Pixel',
              cookies: [
                {
                  name: createRegex(metaPixelCookies),
                },
              ],
            },
            googleAds: {
              label: 'Google Ads',
              cookies: [
                {
                  name: createRegex(googleAdsCookies),
                },
              ],
            },
          },
        },
        analytics: {
          services: {
            googleAnalytics: {
              label: 'Google Analytics',
              cookies: [
                {
                  name: createRegex(googleAnalyticsCookies),
                },
              ],
            },
            microsoftClarity: {
              label: 'Microsoft Clarity',
              cookies: [
                {
                  name: createRegex(microsoftClarityCookies),
                },
              ],
            },
          },
        },
      },
      language: {
        default: 'sk',
        translations: {
          sk: {
            consentModal: {
              title: 'Môžeme, prosím, používať cookies?',
              description:
                'Cookies sú informácie, ktoré ukladáme do vášho zariadenia a zase ich z neho získavame. Niektoré sú naozaj potrebné – bez nich by stránka vôbec nefungovala. Okrem toho používame analytické cookies, ktoré nám umožňujú zisťovať, ako náš web funguje, a stále ho pre vás zlepšovať.',
              acceptAllBtn: 'Povoliť všetko',
              acceptNecessaryBtn: 'Odmietnuť',
              showPreferencesBtn: 'Zobraziť detaily',
            },
            preferencesModal: {
              title: 'Spravovanie cookies',
              acceptAllBtn: 'Povoliť všetko',
              acceptNecessaryBtn: 'Odmietnuť',
              savePreferencesBtn: 'Povoliť vybrané',
              closeIconLabel: 'Zatvoriť',
              sections: [
                {
                  title: 'Potrebné',
                  description:
                    'Tieto cookies zabezpečujú správne fungovanie nášho webu. Napríklad rezervovanie zájazdov. Pomáhajú nám tiež odhaľovať pokusy o neoprávnené prihlásenie a umožňujú efektívne zobrazovanie obsahu.',
                  linkedCategory: 'necessary',
                  cookieTable: {
                    headers: {
                      name: 'Názov',
                      body: 'Popis',
                    },
                    body: [
                      {
                        name: 'XSRF-TOKEN',
                        body: 'Zaisťuje bezpečnosť prehliadania návštevníka tým, že zabraňuje falšovaniu požiadaviek medzi stránkami. Tento súbor cookie je nevyhnutný pre bezpečnosť webovej stránky a návštevníka.',
                      },
                      {
                        name: 'bartotravel_session',
                        body: 'Uchováva stav relácie používateľa pri prechádzaní medzi stránkami.',
                      },
                      {
                        name: 'trip_index_order',
                        body: 'Uchováva stav zoradenia zájazdov.',
                      },
                      {
                        name: 'cc_cookie',
                        body: 'Uchováva nastavenia cookies.',
                      },
                    ],
                  },
                },
                {
                  title: 'Analytické',
                  description:
                    'Tieto cookies nám slúžia na zisťovanie anonymných údajov o návštevnosti nášho webu. Môžu hovoriť o tom, odkiaľ ste k nám prišli, o vyhľadávaniach na našom webe, či ako sa pohybujete po našej stránke, vďaka čomu ju môžeme neustále zlepšovať. Získané údaje nám umožňujú tiež spracovávať štatistiky.',
                  linkedCategory: 'analytics',
                  cookieTable: {
                    headers: cookieTableHeaders,
                    body: analyticalCookies,
                  },
                },
                {
                  title: 'Marketingové',
                  description:
                    'Tieto cookies používame na to, aby sme vám zobrazovali čo najzaujímavejšiu personalizovanú reklamu. Slúžia tiež na vykonávanie obdobných marketingových činností prostredníctvom tretích strán (cez našich reklamných partnerov a sociálne siete), ktorým zdieľame tieto dáta alebo umožňujú našu účasť v sieti affiliate marketingu.',
                  linkedCategory: 'marketing',
                  cookieTable: {
                    headers: cookieTableHeaders,
                    body: marketingCookies,
                  },
                },
              ],
            },
          },
        },
      },
      onConsent: (param) => {
        updateGoogleConsents(param);
      },
      onChange: (param) => {
        updateGoogleConsents(param);
      },
    });

    const changeCookieConsent = document.querySelector('#change-cookie-consent-btn');
    changeCookieConsent?.addEventListener('click', showPreferences);
  }
}
